import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { enrollmentService, academicService } from '../services/api';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import EventAttendanceModal from '../components/EventAttendanceModal';
import { Card, Badge, Button, IconButton, Modal, Spinner, EmptyState, Select, Textarea } from '../components/ui';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useTranslation } from 'react-i18next';

// A stable color per module (hashed from its id) so a student can tell two
// courses' sessions apart on the grid at a glance, without needing a
// per-module color field on the backend.
const MODULE_PALETTE = [
  { chip: 'bg-primary-100 text-primary-700', dot: 'bg-primary-500', ring: 'ring-primary-300' },
  { chip: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', ring: 'ring-emerald-300' },
  { chip: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500', ring: 'ring-purple-300' },
  { chip: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', ring: 'ring-amber-300' },
  { chip: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500', ring: 'ring-rose-300' },
  { chip: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500', ring: 'ring-teal-300' },
  { chip: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500', ring: 'ring-indigo-300' },
  { chip: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', ring: 'ring-orange-300' },
];
function moduleColor(moduleId) {
  const n = Number(moduleId) || 0;
  return MODULE_PALETTE[n % MODULE_PALETTE.length];
}

const EVENT_TYPES = [
  { value: 'class_session', label: 'Class session', tone: 'blue' },
  { value: 'live_session', label: 'Live session', tone: 'green' },
  { value: 'exam', label: 'Exam', tone: 'red' },
  { value: 'assignment_due', label: 'Assignment due', tone: 'yellow' },
  { value: 'meeting', label: 'Meeting', tone: 'purple' },
  { value: 'other', label: 'Other', tone: 'gray' },
];
const EVENT_TYPE_TONE = Object.fromEntries(EVENT_TYPES.map((t) => [t.value, t.tone]));

const RSVP_OPTIONS = [
  { value: 'going', label: 'Going', tone: 'green' },
  { value: 'maybe', label: 'Maybe', tone: 'yellow' },
  { value: 'not_going', label: 'Not going', tone: 'gray' },
];

function toLocalDateInput(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function toLocalTimeInput(date) {
  const d = new Date(date);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const BLANK_FORM = {
  title: '', description: '', module: '', event_type: 'class_session',
  date: '', time: '', end_date: '', end_time: '', location: '', url: '', repeat_weeks: 1,
};

function eventFromForm(form) {
  const start = new Date(`${form.date}T${form.time || '00:00'}`);
  const payload = {
    title: form.title, description: form.description, module: form.module,
    event_type: form.event_type, start_datetime: start.toISOString(),
    location: form.location, url: form.url,
  };
  if (form.end_date) {
    payload.end_datetime = new Date(`${form.end_date}T${form.end_time || form.time || '00:00'}`).toISOString();
  }
  return payload;
}

function isLiveNow(event) {
  if (!event.url) return false;
  const start = new Date(event.start_datetime).getTime();
  const end = event.end_datetime ? new Date(event.end_datetime).getTime() : start + 60 * 60 * 1000;
  const now = Date.now();
  return now >= start - 5 * 60 * 1000 && now <= end;
}

function looksLikeAddress(location) {
  return !!location && !/^https?:\/\//i.test(location);
}

/** A copy-to-clipboard icon button; used for both the location text and the
 * meeting link, since "not copyable" was the exact complaint that started
 * this rebuild. */
function CopyButton({ value, label }) {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      addNotification(t('Copied to clipboard'), 'success');
    } catch (error) {
      addNotification(t('Failed to copy'), 'error');
    }
  };
  return (
    <IconButton
      onClick={copy}
      label={label || t('Copy')}
      color="gray"
      icon={(
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    />
  );
}

function Calendar() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotification();
  const confirm = useConfirm();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [myModules, setMyModules] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState(null);
  const [attendanceEvent, setAttendanceEvent] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteChoiceEvent, setDeleteChoiceEvent] = useState(null);

  const isTeacher = user?.roles?.some((r) => r.name.toLowerCase() === 'teacher');

  const loadEvents = useCallback(() => {
    setLoadingEvents(true);
    enrollmentService.getEvents()
      .then((res) => {
        const data = res.data;
        setEvents(Array.isArray(data) ? data : data?.results || []);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoadingEvents(false));
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    if (!isTeacher) return;
    academicService.getMyModules().then((res) => {
      const data = res.data;
      setMyModules(Array.isArray(data) ? data : data.results || []);
    }).catch(() => setMyModules([]));
  }, [isTeacher]);

  const canManage = useCallback((event) => (
    isTeacher && (event.created_by?.id === user?.id || myModules.some((m) => m.id === event.module))
  ), [isTeacher, myModules, user?.id]);

  // Keep whichever modal is open in sync with the latest fetched copy of
  // its event (e.g. after an RSVP or edit), instead of holding a stale
  // snapshot from the moment it was first opened.
  useEffect(() => {
    if (selectedEvent) {
      const fresh = events.find((e) => e.id === selectedEvent.id);
      if (fresh && fresh !== selectedEvent) setSelectedEvent(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }
  if (!user) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };
  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleString(i18n.language, { month: 'long', year: 'numeric' });

  const eventsByDay = events.reduce((acc, ev) => {
    try {
      const d = new Date(ev.start_datetime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      acc[key] = acc[key] || [];
      acc[key].push(ev);
    } catch (er) { /* ignore parse errors */ }
    return acc;
  }, {});

  const upcoming = [...events]
    .filter((e) => new Date(e.end_datetime || e.start_datetime) >= new Date(Date.now() - 60 * 60 * 1000))
    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
    .slice(0, 8);

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));

  const openCreateForm = (presetDate) => {
    setEditingEvent(null);
    setForm({
      ...BLANK_FORM,
      module: myModules[0]?.id || '',
      date: presetDate ? toLocalDateInput(presetDate) : toLocalDateInput(new Date()),
      time: '09:00',
    });
    setFormOpen(true);
  };

  const openEditForm = (event) => {
    setEditingEvent(event);
    setForm({
      title: event.title, description: event.description || '', module: event.module,
      event_type: event.event_type || 'other',
      date: toLocalDateInput(event.start_datetime), time: toLocalTimeInput(event.start_datetime),
      end_date: event.end_datetime ? toLocalDateInput(event.end_datetime) : '',
      end_time: event.end_datetime ? toLocalTimeInput(event.end_datetime) : '',
      location: event.location || '', url: event.url || '', repeat_weeks: 1,
    });
    setSelectedEvent(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.module || !form.date || !form.time) {
      addNotification(t('Title, module, date and time are required'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = eventFromForm(form);
      if (editingEvent) {
        await enrollmentService.updateEvent(editingEvent.id, payload);
        addNotification(t('Event updated'), 'success');
      } else {
        await enrollmentService.createEvent({ ...payload, repeat_weeks: form.repeat_weeks });
        addNotification(t('Event created'), 'success');
      }
      setFormOpen(false);
      loadEvents();
    } catch (error) {
      addNotification(t('Failed to save the event'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const performDelete = async (event, series) => {
    try {
      await enrollmentService.deleteEvent(event.id, series);
      addNotification(t('Event deleted'), 'success');
      setSelectedEvent(null);
      setSelectedDayEvents(null);
      setDeleteChoiceEvent(null);
      loadEvents();
    } catch (error) {
      addNotification(t('Failed to delete the event'), 'error');
    }
  };

  const handleDelete = async (event) => {
    if (event.recurring_group) {
      // Plain Cancel/Delete (via useConfirm) can't express a 3-way choice,
      // so a recurring event gets its own small dedicated picker instead.
      setDeleteChoiceEvent(event);
      return;
    }
    if (!(await confirm(t('Delete this event? This cannot be undone.')))) return;
    performDelete(event, false);
  };

  const handleRsvp = async (event, status) => {
    try {
      const res = await enrollmentService.rsvpEvent(event.id, status);
      setEvents((prev) => prev.map((e) => (e.id === event.id ? res.data : e)));
    } catch (error) {
      addNotification(t('Failed to save your RSVP'), 'error');
    }
  };

  const handleDownloadIcs = async (event) => {
    try {
      const res = await enrollmentService.downloadEventIcs(event.id);
      const blob = new Blob([res.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.title || 'event'}.ics`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      addNotification(t('Failed to download the calendar file'), 'error');
    }
  };

  const EventChip = ({ ev, dark }) => {
    const c = moduleColor(ev.module);
    return (
      <div
        onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
        className={`text-xs truncate px-1.5 py-0.5 rounded flex items-center gap-1 ${dark ? 'bg-white/20 text-white' : c.chip}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dark ? 'bg-white' : c.dot}`} />
        <span className="truncate">{ev.title}</span>
        {isLiveNow(ev) && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" />}
      </div>
    );
  };

  const EventMeta = ({ event, compact }) => (
    <div className={`flex items-center gap-2 flex-wrap ${compact ? 'text-xs' : 'text-sm'} text-gray-500 mt-1`}>
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${moduleColor(event.module).chip}`}>
        {event.module_name}
      </span>
      <Badge tone={EVENT_TYPE_TONE[event.event_type] || 'gray'}>{t(EVENT_TYPES.find((x) => x.value === event.event_type)?.label || 'Other')}</Badge>
      {event.recurring_group && (
        <span className="inline-flex items-center gap-1 text-gray-400">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {t('Weekly series')}
        </span>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Calendar')} subtitle={t('Manage your schedule and events')} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar grid */}
            <Card className="lg:col-span-2 !p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800 capitalize">{monthName}</h2>
                <div className="flex gap-2">
                  <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                    <svg className="w-5 h-5 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-lg">
                    {t('Today')}
                  </button>
                  <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                    <svg className="w-5 h-5 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-2">
                {[t('Sun'), t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat')].map((day) => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">{day}</div>
                ))}

                {[...Array(firstDay)].map((_, i) => <div key={`empty-${i}`} className="aspect-square" />)}

                {[...Array(daysInMonth)].map((_, i) => {
                  const day = i + 1;
                  const cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                  const key = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}-${String(cellDate.getDate()).padStart(2, '0')}`;
                  const dayEvents = eventsByDay[key] || [];
                  const isToday = day === new Date().getDate() && currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear();
                  return (
                    <div
                      key={day}
                      className={`aspect-square relative rounded-lg cursor-pointer hover:bg-primary-50 transition group ${isToday ? 'bg-primary-600 text-white font-bold' : 'text-gray-700'}`}
                      onClick={() => {
                        if (dayEvents.length > 0) setSelectedDayEvents({ date: cellDate, events: dayEvents });
                        else if (isTeacher) openCreateForm(cellDate);
                      }}
                    >
                      <div className={`absolute top-2 left-2 rtl:left-auto rtl:right-2 text-sm ${isToday ? 'text-white' : 'text-gray-700'}`}>{day}</div>
                      {dayEvents.length === 0 && isTeacher && (
                        <span className="absolute top-1.5 right-1.5 rtl:right-auto rtl:left-1.5 opacity-0 group-hover:opacity-60 text-gray-400 text-lg leading-none">+</span>
                      )}
                      {dayEvents.length > 0 && (
                        <div className="absolute bottom-2 left-2 right-2 space-y-1">
                          {dayEvents.slice(0, 3).map((ev) => <EventChip key={ev.id} ev={ev} dark={isToday} />)}
                          {dayEvents.length > 3 && (
                            <div className={`text-[10px] px-1 ${isToday ? 'text-white/80' : 'text-gray-400'}`}>+{dayEvents.length - 3} {t('more')}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Upcoming events */}
            <Card className="!p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">{t('Upcoming Events')}</h3>
                {isTeacher && (
                  <IconButton
                    onClick={() => openCreateForm()}
                    label={t('Add Event')}
                    color="primary"
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
                  />
                )}
              </div>
              <div className="space-y-1">
                {loadingEvents ? (
                  <div className="p-6 flex justify-center"><Spinner /></div>
                ) : upcoming.length === 0 ? (
                  <EmptyState title={t('No items found', { what: t('Upcoming Events') })} />
                ) : (
                  upcoming.map((event) => (
                    <div key={event.id} onClick={() => setSelectedEvent(event)} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${moduleColor(event.module).dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h4 className="font-semibold text-gray-800 text-sm truncate">{event.title}</h4>
                          {isLiveNow(event) && <span className="text-[10px] font-bold text-red-600 shrink-0">{t('LIVE')}</span>}
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(event.start_datetime).toLocaleDateString(i18n.language)} · {new Date(event.start_datetime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        </main>

        {/* Event Detail Modal */}
        <Modal open={!!selectedEvent} onClose={() => setSelectedEvent(null)} title={selectedEvent?.title} size="lg">
          {selectedEvent && (
            <div className="p-6">
              <EventMeta event={selectedEvent} />
              {selectedEvent.description && <p className="text-gray-700 mt-4 whitespace-pre-wrap">{selectedEvent.description}</p>}

              <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  <span>{new Date(selectedEvent.start_datetime).toLocaleDateString(i18n.language, { dateStyle: 'medium' })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>
                    {new Date(selectedEvent.start_datetime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                    {selectedEvent.end_datetime && ` – ${new Date(selectedEvent.end_datetime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                </div>
              </div>

              {selectedEvent.location && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  {looksLikeAddress(selectedEvent.location) ? (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(selectedEvent.location)}`}
                      target="_blank" rel="noreferrer" className="text-primary-600 hover:underline truncate"
                    >
                      {selectedEvent.location}
                    </a>
                  ) : (
                    <span className="truncate">{selectedEvent.location}</span>
                  )}
                  <CopyButton value={selectedEvent.location} label={t('Copy location')} />
                </div>
              )}

              {selectedEvent.url && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                  <span className="truncate flex-1 text-gray-600">{selectedEvent.url}</span>
                  <CopyButton value={selectedEvent.url} label={t('Copy link')} />
                  <a
                    href={selectedEvent.url} target="_blank" rel="noreferrer"
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white shrink-0 ${isLiveNow(selectedEvent) ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-primary-600 hover:bg-primary-700'}`}
                  >
                    {isLiveNow(selectedEvent) ? t('Join Now') : t('Open link')}
                  </a>
                </div>
              )}

              {!isTeacher && (
                <div className="mt-5">
                  <p className="text-xs font-semibold text-gray-500 mb-2">{t('Are you going?')}</p>
                  <div className="flex gap-2">
                    {RSVP_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleRsvp(selectedEvent, opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                          selectedEvent.my_rsvp === opt.value
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
                        }`}
                      >
                        {t(opt.label)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedEvent.rsvp_summary && (
                <p className="mt-3 text-xs text-gray-400">
                  {t('{{going}} going · {{maybe}} maybe · {{not_going}} not going', {
                    going: selectedEvent.rsvp_summary.going || 0,
                    maybe: selectedEvent.rsvp_summary.maybe || 0,
                    not_going: selectedEvent.rsvp_summary.not_going || 0,
                  })}
                  {Array.isArray(selectedEvent.attendees) && ` · ${t('{{count}} attendees', { count: selectedEvent.attendees.length })}`}
                </p>
              )}

              <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => handleDownloadIcs(selectedEvent)}>{t('Add to calendar')}</Button>
                {canManage(selectedEvent) && (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => setAttendanceEvent(selectedEvent)}>{t('Take Attendance')}</Button>
                    <Button size="sm" variant="secondary" onClick={() => openEditForm(selectedEvent)}>{t('Edit')}</Button>
                    <Button size="sm" variant="danger" onClick={() => handleDelete(selectedEvent)}>{t('Delete')}</Button>
                  </>
                )}
              </div>
            </div>
          )}
        </Modal>

        {/* Day Events Modal */}
        <Modal
          open={!!selectedDayEvents}
          onClose={() => setSelectedDayEvents(null)}
          title={selectedDayEvents ? selectedDayEvents.date.toLocaleDateString(i18n.language, { dateStyle: 'full' }) : ''}
        >
          {selectedDayEvents && (
            <div className="p-6 space-y-3">
              {selectedDayEvents.events.map((ev) => (
                <div key={ev.id} className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedEvent(ev)}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-gray-800 flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${moduleColor(ev.module).dot}`} />
                      {ev.title}
                      {isLiveNow(ev) && <span className="text-[10px] font-bold text-red-600">{t('LIVE')}</span>}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{new Date(ev.start_datetime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <EventMeta event={ev} compact />
                  {ev.location && <p className="text-xs text-gray-500 mt-1.5 truncate">📍 {ev.location}</p>}
                </div>
              ))}
              {isTeacher && (
                <Button size="sm" variant="secondary" onClick={() => { openCreateForm(selectedDayEvents.date); setSelectedDayEvents(null); }}>
                  + {t('Add Event')}
                </Button>
              )}
            </div>
          )}
        </Modal>

        {/* Create / Edit Event Modal */}
        <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editingEvent ? t('Edit Event') : t('Add Event')}>
          <div className="p-6 space-y-3">
            <input className="w-full p-2.5 border border-gray-200 rounded-lg text-sm" placeholder={t('Title')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea rows={2} placeholder={t('Description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} disabled={!!editingEvent}>
                <option value="">{t('Select a module')}</option>
                {myModules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Select>
              <Select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
                {EVENT_TYPES.map((opt) => <option key={opt.value} value={opt.value}>{t(opt.label)}</option>)}
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              <input type="time" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            </div>
            <p className="text-xs font-medium text-gray-400">{t('Ends (optional)')}</p>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              <input type="time" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </div>
            <input className="w-full p-2.5 border border-gray-200 rounded-lg text-sm" placeholder={t('Location')} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <input className="w-full p-2.5 border border-gray-200 rounded-lg text-sm" placeholder={t('Meeting link (optional)')} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            {!editingEvent && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('Repeat weekly for how many sessions?')}</label>
                <input
                  type="number" min={1} max={26} className="w-24 p-2.5 border border-gray-200 rounded-lg text-sm"
                  value={form.repeat_weeks} onChange={(e) => setForm({ ...form, repeat_weeks: e.target.value })}
                />
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button loading={saving} onClick={handleSave}>{editingEvent ? t('Save changes') : t('Create Event')}</Button>
              <Button variant="secondary" onClick={() => setFormOpen(false)}>{t('Cancel')}</Button>
            </div>
          </div>
        </Modal>

        {/* Recurring-event delete choice */}
        <Modal open={!!deleteChoiceEvent} onClose={() => setDeleteChoiceEvent(null)} title={t('Delete recurring event')} size="sm">
          {deleteChoiceEvent && (
            <div className="p-6 space-y-3">
              <p className="text-sm text-gray-600">{t('This event is part of a weekly series. What would you like to delete?')}</p>
              <div className="flex flex-col gap-2 pt-2">
                <Button variant="danger" onClick={() => performDelete(deleteChoiceEvent, false)}>{t('Just this session')}</Button>
                <Button variant="danger" onClick={() => performDelete(deleteChoiceEvent, true)}>{t('The entire series')}</Button>
                <Button variant="secondary" onClick={() => setDeleteChoiceEvent(null)}>{t('Cancel')}</Button>
              </div>
            </div>
          )}
        </Modal>

        <EventAttendanceModal event={attendanceEvent} onClose={() => setAttendanceEvent(null)} />
      </div>
    </div>
  );
}

export default Calendar;
